import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import LoadingCircular from "components/LoadingCircular";
import PageTitle from "components/typography/PageTitle";
import usePageTitle from "hooks/usePageTitle";
import BackToPage from "components/typography/BackToPage";
import axios from "utils/axios";
import { Box, Divider, List, ListItem, ListItemText, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { getUnitLabel } from "utils/projectUnit";

const ListText = styled(ListItemText)(({ theme }) => ({
  color: "#454745",
  "& .MuiTypography-root": {
    fontSize: "1rem",
  },
}));

const ListAmount = styled("div")(({ theme }) => ({
  color: "#2c3340",
  fontSize: "1rem",
}));

export default function ProjectDetails() {
  const [, setPageTitle] = usePageTitle("Project Details");
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState({});

  //get project details
  useEffect(() => {
    const getProject = async () => {
      try {
        const { data } = await axios.get(`api/project/details/${projectId}`);

        setProject(data.project);
        setPageTitle(`${data.project.projectName}`);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    getProject();
  }, [projectId, setPageTitle]);

  //loading
  if (loading) {
    return <LoadingCircular />;
  }

  const {
    constructionServiceChargeAmount,
    expenseAmount,
    landPaidAmount,
    landPrice,
    landExpense,
    totalFlat,
    isSquareFeet,
    serviceChargePercentage,
    payOrderServiceCharge,
    payOrderAmount,
    paidAmount,
  } = project;

  const totalPayorder = payOrderAmount + payOrderServiceCharge;
  const totalDue = totalPayorder - paidAmount;
  const landDue = landPrice - landPaidAmount;

  return (
    <Box sx={{ maxWidth: "md" }}>
      <PageTitle mb="0">{project?.projectName}</PageTitle>
      <Typography variant="body2">{project?.location}</Typography>

      <BackToPage to="/project/list" />

      <Paper sx={{ p: 2 }}>
        <List>
          <ListItem>
            <ListText primary={getUnitLabel(isSquareFeet)} />
            <ListAmount>{totalFlat}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText primary="Expense Amount" />
            <ListAmount>৳{expenseAmount?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText primary="Pay Order Amount" />
            <ListAmount>৳{payOrderAmount?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText primary={`Pay Order Service Charge (${serviceChargePercentage}%)`} />
            <ListAmount>৳{payOrderServiceCharge?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText
              sx={{
                "& .MuiTypography-root": {
                  fontWeight: "bold",
                },
              }}
              primary="Total Pay Order"
            />
            <ListAmount sx={{ fontWeight: "bold" }}>৳{totalPayorder?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText
              sx={{
                "& .MuiTypography-root": {
                  fontWeight: "bold",
                },
              }}
              primary="Paid Amount"
            />
            <ListAmount sx={{ fontWeight: "bold" }}>৳{paidAmount?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            {totalDue >= 0 ? (
              <>
                <ListText
                  sx={{
                    "& .MuiTypography-root": {
                      fontWeight: "bold",
                      color: "#db2055",
                    },
                  }}
                  primary="Due Amount"
                />
                <ListAmount sx={{ fontWeight: "bold", color: "#db2055" }}>৳{totalDue?.toFixed(2)}</ListAmount>
              </>
            ) : (
              <>
                <ListText
                  sx={{
                    "& .MuiTypography-root": {
                      fontWeight: "bold",
                      color: "#00C851",
                    },
                  }}
                  primary="Advance Amount"
                />
                <ListAmount sx={{ fontWeight: "bold", color: "#00C851" }}>৳{Math.abs(totalDue).toFixed(2)}</ListAmount>
              </>
            )}
          </ListItem>
          <Divider />

          <ListItem>
            <ListText primary="Land Expense Amount" />
            <ListAmount>৳{landExpense?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText primary="Land Price" />
            <ListAmount>৳{landPrice?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText primary="land Paid Amount" />
            <ListAmount>৳{landPaidAmount?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText
              sx={{
                "& .MuiTypography-root": {
                  fontWeight: "bold",
                  color: "#db2055",
                },
              }}
              primary="Land Due Amount"
            />
            <ListAmount sx={{ fontWeight: "bold", color: "#db2055" }}>৳{landDue?.toFixed(2)}</ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText
              sx={{
                "& .MuiTypography-root": {
                  fontWeight: "bold",
                  color: "#00C851",
                },
              }}
              primary="Land Profit Amount"
            />
            <ListAmount sx={{ fontWeight: "bold", color: "#00C851" }}>
              ৳{(landPrice - landExpense)?.toFixed(2)}
            </ListAmount>
          </ListItem>
          <Divider />

          <ListItem>
            <ListText primary="Construction Service Charge" />
            <ListAmount>৳{constructionServiceChargeAmount?.toFixed(2)}</ListAmount>
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
}
